"use client";

import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronUp,
  Clock3,
  Download,
  ExternalLink,
  Eye,
  FileText,
  MapPin,
  Play,
  UserRound,
  Video,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  normalizeStatus,
  getStatusDisplayValue,
} from "../../utils/applicationStatus";

import type {
  CandidateApplication,
  CandidateInterview,
} from "../../types/candidateDashboard.types";
import { formatEmploymentType } from "@/modules/jobs/utils/jobFormatter";

import {
  downloadCandidateResume,
  downloadCandidateVideo,
  downloadInterviewAttachment,
} from "../../api/candidateDashboard.api";
import { downloadBlob, openBlobInNewTab } from "@/shared/utils/fileActions";

interface AppliedJobsProps {
  applications: CandidateApplication[];
}

/* ------------------------------------------------------------------ */
/* Styles / helpers (logic unchanged)                                  */
/* ------------------------------------------------------------------ */

const statusStyles: Record<string, string> = {
  APPLIED: "bg-blue-50 text-blue-700 ring-blue-200",
  UNDER_REVIEW: "bg-violet-50 text-violet-700 ring-violet-200",
  SHORTLISTED: "bg-emerald-50 text-emerald-700 ring-emerald-200",

  "SHORTLISTED FOR HR ROUND": "bg-blue-50 text-blue-700 ring-blue-200",
  "SHORTLISTED FOR TECHNICAL ROUND": "bg-indigo-50 text-indigo-700 ring-indigo-200",
  "SHORTLISTED FOR MANAGEMENT ROUND": "bg-purple-50 text-purple-700 ring-purple-200",

  SELECTED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  REJECTED: "bg-red-50 text-red-700 ring-red-200",

  "ATS SCREENING": "bg-violet-50 text-violet-700 ring-violet-200",
  "NOT SELECTED": "bg-red-50 text-red-700 ring-red-200",
  CONSIDER: "bg-amber-50 text-amber-700 ring-amber-200",
  "HR INTERVIEW SCHEDULED": "bg-blue-50 text-blue-700 ring-blue-200",
  HIRED: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  BLACKLISTED: "bg-red-50 text-red-700 ring-red-200",
};

function formatDate(date?: string | null) {
  if (!date) return "-";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTime(time?: string | null) {
  if (!time) return "-";

  const parts = time.split(":");

  if (parts.length < 2) {
    return time;
  }

  const hour = Number(parts[0]);
  const minute = parts[1];

  if (Number.isNaN(hour)) {
    return time;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${displayHour}:${minute} ${period}`;
}

function getDateParts(date?: string | null) {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    day: parsed.toLocaleDateString("en-IN", { day: "2-digit" }),
    month: parsed.toLocaleDateString("en-IN", { month: "short" }),
  };
}

function getRoundName(
  round?: string,
  workflow?: {
    key: string;
    name: string;
    sequence: number;
  }[]
) {
  if (!round) return "-";

  const configuredRound = workflow?.find(
    (item) => item.key.toUpperCase() === round.toUpperCase()
  );

  if (configuredRound?.name) {
    return configuredRound.name;
  }

  switch (round.toUpperCase()) {
    case "HR":
      return "HR Round";

    case "TECHNICAL":
      return "Technical Round";

    case "MANAGEMENT":
      return "Management Round";

    default:
      return round;
  }
}

function getInterviewStatusLabel(status?: string) {
  if (!status) return "Pending";

  switch (status.toUpperCase()) {
    case "SCHEDULED":
      return "Scheduled";

    case "RESCHEDULED":
      return "Rescheduled";

    case "IN_PROGRESS":
      return "In Progress";

    case "COMPLETED":
      return "Completed";

    case "CANCELLED":
      return "Cancelled";

    case "NO_SHOW":
      return "No Show";

    default:
      return status;
  }
}

function getInterviewStatusStyle(status?: string) {
  switch (status?.toUpperCase()) {
    case "SCHEDULED":
      return "bg-blue-50 text-blue-700 ring-blue-200";

    case "RESCHEDULED":
      return "bg-amber-50 text-amber-700 ring-amber-200";

    case "IN_PROGRESS":
      return "bg-indigo-50 text-indigo-700 ring-indigo-200";

    case "COMPLETED":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200";

    case "CANCELLED":
    case "NO_SHOW":
      return "bg-red-50 text-red-700 ring-red-200";

    default:
      return "bg-slate-100 text-slate-600 ring-slate-200";
  }
}

function getModeLabel(mode?: string) {
  if (!mode) return "-";

  switch (mode.toUpperCase()) {
    case "ONLINE":
      return "Online";

    case "OFFLINE":
      return "Offline";

    case "PHONE":
      return "Telephonic";

    default:
      return mode;
  }
}

function getResultStyle(result?: string | null) {
  switch (result?.toUpperCase()) {
    case "PASSED":
      return "text-emerald-600";

    case "FAILED":
      return "text-red-600";

    default:
      return "text-slate-700";
  }
}

function isInterviewUpcoming(interview: CandidateInterview) {
  if (!interview.date) return false;

  const interviewDate = new Date(
    `${interview.date}T${interview.time || "00:00"}`
  );

  if (Number.isNaN(interviewDate.getTime())) {
    return false;
  }

  return (
    interviewDate.getTime() > Date.now() &&
    ["SCHEDULED", "RESCHEDULED"].includes(
      interview.status?.toUpperCase() || ""
    )
  );
}

type TimelineStage = {
  key: string;
  name: string;
  kind: "status" | "round";
};

const TERMINAL_APPLICATION_STATUSES = [
  "REJECTED",
  "NOT SELECTED",
  "BLACKLISTED",
];

function normalizeTimelineStatus(value?: string | null) {
  return (value || "").trim().toUpperCase().replace(/[_-]+/g, " ");
}

function getTimelineStages(application: CandidateApplication): TimelineStage[] {
  const rounds = [...(application.job?.interviewWorkflow ?? [])]
    .sort((a, b) => a.sequence - b.sequence);

  const stages: TimelineStage[] = [
    { key: "APPLIED", name: "Applied", kind: "status" },
    { key: "UNDER_REVIEW", name: "Under Review", kind: "status" },
    { key: "SHORTLISTED", name: "Shortlisted", kind: "status" },
    ...rounds.map((round) => ({
      key: round.key,
      name: round.name || getRoundName(round.key, rounds),
      kind: "round" as const,
    })),
    { key: "SELECTED", name: "Selected", kind: "status" },
  ];

  const uniqueStages = stages.filter(
    (stage, index, all) =>
      all.findIndex((item) => item.key.toUpperCase() === stage.key.toUpperCase()) === index
  );

  const currentStatus = normalizeTimelineStatus(
    application.currentStatus || getStatusDisplayValue(application.status)
  );

  if (!TERMINAL_APPLICATION_STATUSES.includes(currentStatus)) {
    return uniqueStages;
  }

  // Place the terminal state immediately after the last known process stage.
  // This keeps all stages after rejection/blacklisting hidden.
  const history = application.statusHistory ?? [];
  const terminalHistoryIndex = history.findIndex(
    (item) => normalizeTimelineStatus(item.status) === currentStatus
  );
  const priorHistory = terminalHistoryIndex >= 0
    ? history.slice(0, terminalHistoryIndex)
    : history.filter((item) => !TERMINAL_APPLICATION_STATUSES.includes(normalizeTimelineStatus(item.status)));

  let lastReachedIndex = 1; // Under Review is the safest fallback after an application was reviewed.
  for (const item of priorHistory) {
    const status = normalizeTimelineStatus(item.status);
    if (status === "APPLIED") lastReachedIndex = Math.max(lastReachedIndex, 0);
    if (["UNDER REVIEW", "ATS SCREENING", "CONSIDER"].includes(status)) {
      lastReachedIndex = Math.max(lastReachedIndex, 1);
    }
    if (status.includes("SHORTLISTED")) {
      lastReachedIndex = Math.max(lastReachedIndex, 2);
    }

    const roundIndex = uniqueStages.findIndex((stage) =>
      stage.kind === "round" && (
        status.includes(normalizeTimelineStatus(stage.key)) ||
        status.includes(normalizeTimelineStatus(stage.name))
      )
    );
    if (roundIndex >= 0) lastReachedIndex = Math.max(lastReachedIndex, roundIndex);
    if (["SELECTED", "HIRED"].includes(status)) {
      lastReachedIndex = uniqueStages.length - 1;
    }
  }

  // Interview records can locate the process position when status history is sparse.
  const latestInterview = application.latestInterview;
  if (latestInterview?.round) {
    const interviewStageIndex = uniqueStages.findIndex(
      (stage) => stage.kind === "round" &&
        normalizeTimelineStatus(stage.key) === normalizeTimelineStatus(latestInterview.round)
    );
    if (interviewStageIndex >= 0) lastReachedIndex = Math.max(lastReachedIndex, interviewStageIndex);
  }

  const terminalName = currentStatus === "NOT SELECTED"
    ? "Not Selected"
    : currentStatus.charAt(0) + currentStatus.slice(1).toLowerCase();

  return [
    ...uniqueStages.slice(0, Math.min(lastReachedIndex + 1, uniqueStages.length - 1)),
    { key: currentStatus, name: terminalName, kind: "status" },
  ];
}

function getTimelineStageState(
  stage: TimelineStage,
  index: number,
  stages: TimelineStage[],
  application: CandidateApplication
): "completed" | "current" | "future" {
  const normalize = normalizeTimelineStatus;
  const currentStatus = normalize(
    application.currentStatus || getStatusDisplayValue(application.status)
  );
  const historyStatuses = (application.statusHistory ?? []).map((item) => normalize(item.status));
  const stageKey = normalize(stage.key);

  if (TERMINAL_APPLICATION_STATUSES.includes(currentStatus)) {
    return stageKey === currentStatus ? "current" : "completed";
  }

  if (stage.kind === "round") {
    const interviews = [
      ...(application.interviewHistory ?? []),
      ...(application.latestInterview ? [application.latestInterview] : []),
    ].filter((interview) => normalize(interview.round) === stageKey);
    const latestInterview = interviews[interviews.length - 1];
    const interviewStatus = normalize(latestInterview?.status);

    if (interviewStatus === "COMPLETED") return "completed";
    if (["SCHEDULED", "RESCHEDULED", "IN PROGRESS"].includes(interviewStatus)) return "current";

    if (historyStatuses.some((status) =>
      status.includes(stageKey) || status.includes(normalize(stage.name))
    )) return "completed";

    if (currentStatus.includes(stageKey) || currentStatus.includes(normalize(stage.name))) return "current";

    const laterRoundIsActive = stages.slice(index + 1).some((laterStage) => {
      if (laterStage.kind !== "round") return false;
      const laterKey = normalize(laterStage.key);
      const laterInterview = [
        ...(application.interviewHistory ?? []),
        ...(application.latestInterview ? [application.latestInterview] : []),
      ].filter((item) => normalize(item.round) === laterKey)
        .slice(-1)[0];
      return normalize(laterInterview?.status) === "COMPLETED" ||
        ["SCHEDULED", "RESCHEDULED", "IN PROGRESS"].includes(normalize(laterInterview?.status)) ||
        historyStatuses.some((status) => status.includes(laterKey));
    });
    return laterRoundIsActive ? "completed" : "future";
  }

  if (stageKey === "APPLIED") {
    return currentStatus === "APPLIED" ? "current" : "completed";
  }

  if (stageKey === "UNDER REVIEW") {
    if (["UNDER REVIEW", "ATS SCREENING", "CONSIDER"].includes(currentStatus)) return "current";
    if (historyStatuses.some((status) => ["UNDER REVIEW", "ATS SCREENING", "CONSIDER"].includes(status)) ||
      currentStatus.includes("SHORTLISTED") || currentStatus.includes("INTERVIEW") ||
      ["SELECTED", "HIRED"].includes(currentStatus)) return "completed";
    return "future";
  }

  if (stageKey === "SHORTLISTED") {
    if (currentStatus === "SHORTLISTED" || currentStatus.includes("SHORTLISTED FOR")) return "current";
    if (historyStatuses.some((status) => status.includes("SHORTLISTED")) ||
      currentStatus.includes("INTERVIEW") || ["SELECTED", "HIRED"].includes(currentStatus)) return "completed";
    return "future";
  }

  if (stageKey === "SELECTED") {
    if (["SELECTED", "HIRED"].includes(currentStatus)) return "current";
    if (historyStatuses.some((status) => ["SELECTED", "HIRED"].includes(status))) return "completed";
    return "future";
  }

  return "future";
}

function getNextAction(application: CandidateApplication) {
  const status = normalizeTimelineStatus(
    application.currentStatus || getStatusDisplayValue(application.status)
  );
  const interview = application.latestInterview;

  if (TERMINAL_APPLICATION_STATUSES.includes(status)) return null;
  if (["SELECTED", "HIRED"].includes(status)) return "Application process completed";

  if (interview) {
    const interviewStatus = normalizeTimelineStatus(interview.status);
    if (["SCHEDULED", "RESCHEDULED"].includes(interviewStatus)) {
      return `Attend ${getRoundName(interview.round, application.job?.interviewWorkflow)}`;
    }
    if (interviewStatus === "IN PROGRESS") {
      return `Complete ${getRoundName(interview.round, application.job?.interviewWorkflow)}`;
    }
  }

  if (status.includes("INTERVIEW SCHEDULED")) return "Attend your scheduled interview";
  if (status.includes("INTERVIEW RESCHEDULED")) return "Attend your rescheduled interview";
  if (status.includes("SHORTLISTED")) return "Wait for interview scheduling";
  if (["APPLIED", "UNDER REVIEW", "ATS SCREENING", "CONSIDER"].includes(status)) {
    return "Application is under review";
  }
  return "No action required right now";
}

/* ------------------------------------------------------------------ */
/* Section wrapper (collapsible block used inside a card)              */
/* ------------------------------------------------------------------ */

function SectionHeader({
  title,
  subtitle,
  right,
  isOpen,
  onToggle,
  toggleLabel,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  toggleLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <h4 className="text-[13px] font-semibold text-slate-900">{title}</h4>
        {subtitle && (
          <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {right}
        <button
          type="button"
          onClick={onToggle}
          aria-label={toggleLabel}
          className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
        >
          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Progress timeline (stepper)                                         */
/* ------------------------------------------------------------------ */

function ProgressTimeline({ application }: { application: CandidateApplication }) {
  const stages = getTimelineStages(application);
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="pt-4">
      <SectionHeader
        title="Application progress"
        subtitle="Track where you are in the hiring process"
        isOpen={isOpen}
        onToggle={() => setIsOpen((value) => !value)}
        toggleLabel={
          isOpen ? "Collapse application progress" : "Expand application progress"
        }
      />

      {isOpen && (
        <div className="mt-3.5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
          <ol className="flex min-w-[560px] items-start">
            {stages.map((stage, index) => {
              const state = getTimelineStageState(stage, index, stages, application);
              const isLast = index === stages.length - 1;
              const stageKey = normalizeTimelineStatus(stage.key);
              const isNegative = TERMINAL_APPLICATION_STATUSES.includes(stageKey);
              const isPositive = ["SELECTED", "HIRED"].includes(stageKey) && state === "current";
              const nextState = !isLast
                ? getTimelineStageState(stages[index + 1], index + 1, stages, application)
                : "future";

              let circle =
                "border-slate-300 bg-white text-slate-400";
              if (state === "completed") {
                circle = "border-blue-600 bg-blue-600 text-white";
              } else if (state === "current") {
                circle = isNegative
                  ? "border-red-500 bg-red-500 text-white ring-4 ring-red-100"
                  : isPositive
                    ? "border-emerald-500 bg-emerald-500 text-white ring-4 ring-emerald-100"
                    : "border-blue-600 bg-white text-blue-600 ring-4 ring-blue-100";
              }

              return (
                <li key={stage.key} className="relative flex-1">
                  {!isLast && (
                    <div
                      className={`absolute left-1/2 top-[11px] h-0.5 w-full ${
                        state === "completed" && nextState !== "future"
                          ? "bg-blue-600"
                          : state === "completed"
                            ? "bg-gradient-to-r from-blue-600 to-slate-200"
                            : "bg-slate-200"
                      }`}
                    />
                  )}

                  <div className="relative z-10 flex flex-col items-center px-1">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${circle}`}
                    >
                      {state === "completed" ? (
                        <Check size={12} strokeWidth={3} />
                      ) : state === "current" && isNegative ? (
                        <X size={12} strokeWidth={3} />
                      ) : state === "current" && isPositive ? (
                        <Check size={12} strokeWidth={3} />
                      ) : (
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            state === "current" ? "bg-blue-600" : "bg-slate-300"
                          }`}
                        />
                      )}
                    </div>

                    <p
                      className={`mt-1.5 max-w-[100px] text-center text-[11px] font-semibold leading-4 ${
                        state === "future" ? "text-slate-400" : "text-slate-800"
                      }`}
                    >
                      {stage.name}
                    </p>

                    <p
                      className={`mt-0.5 text-[10px] ${
                        state === "current"
                          ? isNegative
                            ? "font-semibold text-red-600"
                            : isPositive
                              ? "font-semibold text-emerald-600"
                              : "font-semibold text-blue-600"
                          : state === "completed"
                            ? "text-slate-500"
                            : "text-slate-400"
                      }`}
                    >
                      {state === "current"
                        ? "Current"
                        : state === "completed"
                          ? "Completed"
                          : "Upcoming"}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500">
        {icon}
      </div>

      <div className="min-w-0">
        <p className="text-[11px] text-slate-500">{label}</p>

        <p
          className="mt-0.5 truncate text-[13px] font-medium text-slate-900"
          title={value}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function StatusPill({
  className,
  children,
  small = false,
}: {
  className: string;
  children: React.ReactNode;
  small?: boolean;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full font-semibold ring-1 ring-inset ${
        small ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs"
      } ${className}`}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Current / latest interview                                          */
/* ------------------------------------------------------------------ */

function InterviewDetails({
  interview,
  job,
  applicationStatus,
  className = "mt-6",
}: {
  interview: CandidateInterview;
  job: CandidateApplication["job"];
  applicationStatus: string;
  className?: string;
}) {
  const [showRescheduleHistory, setShowRescheduleHistory] = useState(false);

  const upcoming = isInterviewUpcoming(interview);

  const isOnline = interview.mode?.toUpperCase() === "ONLINE";
  const isOffline = interview.mode?.toUpperCase() === "OFFLINE";

  const interviewStatus = interview.status?.trim().toUpperCase() || "";

  const normalizedApplicationStatus =
    applicationStatus?.trim().toUpperCase() || "";

  const isInterviewCompleted = interviewStatus === "COMPLETED";

  const isCandidateBlocked =
    normalizedApplicationStatus === "REJECTED" ||
    normalizedApplicationStatus === "NOT SELECTED" ||
    normalizedApplicationStatus === "BLACKLISTED";

  const isJoinInterviewDisabled = isInterviewCompleted || isCandidateBlocked;

  const dateParts = getDateParts(interview.date);

  const handleOpenAttachment = async (
    attachment: NonNullable<CandidateInterview["attachments"]>[number]
  ) => {
    try {
      const response = await downloadInterviewAttachment(attachment.id);

      const contentType = String(
        response.headers["content-type"] || "application/octet-stream"
      ).toLowerCase();

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const blobUrl = URL.createObjectURL(blob);

      const newWindow = window.open("", "_blank");

      if (!newWindow) {
        URL.revokeObjectURL(blobUrl);
        return;
      }

      if (contentType.startsWith("video/")) {
        newWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh;">
        <video controls autoplay style="max-width:100%;max-height:100%;">
          <source src="${blobUrl}" type="${contentType}" />
        </video>
      </body>
    </html>
  `);
      } else {
        newWindow.location.href = blobUrl;
      }

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
    }
  };

  return (
    <div
      className={`${className} overflow-hidden rounded-xl border bg-white ${
        upcoming ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"
      }`}
    >
      {/* Interview Header */}
      <div
        className={`flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
          upcoming ? "border-blue-100 bg-blue-50/60" : "border-slate-100 bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg ${
              upcoming ? "bg-blue-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"
            }`}
          >
            {dateParts ? (
              <>
                <span className="text-sm font-bold leading-none">
                  {dateParts.day}
                </span>
                <span className="mt-0.5 text-[10px] font-medium uppercase leading-none opacity-80">
                  {dateParts.month}
                </span>
              </>
            ) : (
              <Video size={18} />
            )}
          </div>

          <div>
            <p
              className={`text-xs font-semibold ${
                upcoming ? "text-blue-700" : "text-slate-500"
              }`}
            >
              {upcoming ? "Upcoming interview" : "Latest interview"}
            </p>

            <h4 className="mt-0.5 text-sm font-semibold text-slate-900">
              {getRoundName(interview.round, job?.interviewWorkflow)}
            </h4>
          </div>
        </div>

        <StatusPill className={getInterviewStatusStyle(interview.status)}>
          {getInterviewStatusLabel(interview.status)}
        </StatusPill>
      </div>

      <div className="p-4">
        {/* Primary Schedule */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <DetailItem
            icon={<CalendarDays size={15} />}
            label="Date"
            value={formatDate(interview.date)}
          />

          <DetailItem
            icon={<Clock3 size={15} />}
            label="Time"
            value={formatTime(interview.time)}
          />

          <DetailItem
            icon={<Clock3 size={15} />}
            label="Duration"
            value={
              interview.duration !== undefined && interview.duration !== null
                ? `${interview.duration} min`
                : "-"
            }
          />

          <DetailItem
            icon={<UserRound size={15} />}
            label="Interviewer"
            value={interview.interviewer?.name || "Not assigned"}
          />
        </div>

        {/* Secondary Details */}
        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
          <DetailItem
            icon={<Video size={15} />}
            label="Mode"
            value={getModeLabel(interview.mode)}
          />

          {isOnline && interview.meetingPlatform && (
            <DetailItem
              icon={<Video size={15} />}
              label="Platform"
              value={interview.meetingPlatform}
            />
          )}

          {isOffline && interview.venue && (
            <DetailItem
              icon={<MapPin size={15} />}
              label="Venue"
              value={interview.venue}
            />
          )}

          {isOffline && interview.address && (
            <DetailItem
              icon={<MapPin size={15} />}
              label="Address"
              value={interview.address}
            />
          )}
        </div>

        {/* Online Action */}
        {isOnline && interview.meetingLink && (
          <div className="mt-4 flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3.5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                Online interview link
              </p>

              <p className="mt-0.5 text-xs text-slate-500">
                {isInterviewCompleted
                  ? "Meeting already done and closed."
                  : isCandidateBlocked
                    ? "Interview is no longer available."
                    : "Use the button to join your scheduled interview."}
              </p>
            </div>

            {isJoinInterviewDisabled ? (
              <button
                type="button"
                disabled
                className="inline-flex h-9 cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-slate-200 px-5 text-sm font-semibold text-slate-500"
              >
                <ExternalLink size={14} />
                {isInterviewCompleted ? "Meeting Closed" : "Interview Unavailable"}
              </button>
            ) : (
              <a
                href={interview.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                <ExternalLink size={14} />
                Join Interview
              </a>
            )}
          </div>
        )}

        {/* Offline Map */}
        {isOffline && interview.googleMapsLocation && (
          <div className="mt-4">
            <a
              href={interview.googleMapsLocation}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
            >
              <MapPin size={15} />
              View location on map
            </a>
          </div>
        )}

        {/* Instructions */}
        {interview.instructions && (
          <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50/60 p-3.5">
            <p className="text-xs font-semibold text-amber-800">Instructions</p>

            <p className="mt-1 text-sm leading-6 text-slate-700">
              {interview.instructions}
            </p>
          </div>
        )}

        {/* Remarks */}
        {interview.remarks && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-slate-500">Remarks</p>

            <p className="mt-1 text-sm leading-6 text-slate-700">
              {interview.remarks}
            </p>
          </div>
        )}

        {/* Result */}
        {interview.result && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3">
            <span className="text-sm text-slate-600">Interview result</span>

            <span
              className={`text-sm font-bold ${getResultStyle(interview.result)}`}
            >
              {interview.result}
            </span>
          </div>
        )}

        {/* Attachments */}
        {interview.attachments && interview.attachments.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 flex items-center gap-2">
              <FileText size={15} className="text-slate-500" />
              <p className="text-sm font-semibold text-slate-900">
                Interview attachments
              </p>
            </div>

            <div className="space-y-2">
              {interview.attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <FileText size={16} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm font-medium text-slate-900"
                      title={attachment.fileName}
                    >
                      {attachment.fileName}
                    </p>

                    <p className="mt-0.5 text-xs text-slate-500">
                      {attachment.fileType}
                      {attachment.fileSize ? ` • ${attachment.fileSize}` : ""}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOpenAttachment(attachment)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 px-3.5 py-1.5 text-sm font-semibold text-blue-600 transition hover:bg-blue-50"
                  >
                    <ExternalLink size={14} />
                    Open
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reschedule History */}
        {interview.rescheduleHistory &&
          interview.rescheduleHistory.length > 0 && (
            <div className="mt-5 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setShowRescheduleHistory((value) => !value)}
                className="flex w-full items-center justify-between text-left"
              >
                <span className="text-sm font-semibold text-slate-800">
                  Reschedule history ({interview.rescheduleHistory.length})
                </span>

                {showRescheduleHistory ? (
                  <ChevronUp size={16} className="text-slate-400" />
                ) : (
                  <ChevronDown size={16} className="text-slate-400" />
                )}
              </button>

              {showRescheduleHistory && (
                <div className="mt-3 space-y-3">
                  {interview.rescheduleHistory.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-slate-200 bg-slate-50 p-3.5"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="flex-1">
                          <p className="text-xs text-slate-500">
                            Previous schedule
                          </p>

                          <p className="mt-0.5 text-sm font-medium text-slate-500 line-through decoration-slate-300">
                            {formatDate(item.previousSchedule?.date)}
                            {" • "}
                            {formatTime(item.previousSchedule?.time)}
                          </p>
                        </div>

                        <ArrowRight
                          size={16}
                          className="hidden shrink-0 text-slate-400 sm:block"
                        />

                        <div className="flex-1">
                          <p className="text-xs text-slate-500">New schedule</p>

                          <p className="mt-0.5 text-sm font-semibold text-slate-900">
                            {formatDate(item.newSchedule?.date)}
                            {" • "}
                            {formatTime(item.newSchedule?.time)}
                          </p>
                        </div>
                      </div>

                      {item.reason && (
                        <div className="mt-3 border-t border-slate-200 pt-3">
                          <p className="text-xs text-slate-500">Reason</p>

                          <p className="mt-0.5 text-sm leading-6 text-slate-700">
                            {item.reason}
                          </p>
                        </div>
                      )}

                      {item.rescheduledAt && (
                        <p className="mt-2 text-xs text-slate-400">
                          Rescheduled on {formatDate(item.rescheduledAt)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Interview: one-line row + details popup                         */
/* ------------------------------------------------------------------ */

function InterviewSummaryRow({
  interview,
  job,
  onViewDetails,
}: {
  interview: CandidateInterview;
  job: CandidateApplication["job"];
  onViewDetails: () => void;
}) {
  return (
    <div className="mt-3.5 flex items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-blue-600 ring-1 ring-blue-100">
          <Video size={14} />
        </div>

        <p className="truncate text-[13px] font-semibold text-slate-900">
          {getRoundName(interview.round, job?.interviewWorkflow)}
        </p>
      </div>

      <button
        type="button"
        onClick={onViewDetails}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-blue-200 bg-white px-2.5 py-1 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
      >
        View details
        <ArrowRight size={12} />
      </button>
    </div>
  );
}

function InterviewModal({
  interview,
  job,
  applicationStatus,
  onClose,
}: {
  interview: CandidateInterview;
  job: CandidateApplication["job"];
  applicationStatus: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3 backdrop-blur-sm sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Interview details"
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        {/* Modal header */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              Interview details
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {job?.title || "-"}
              {job?.department ? ` • ${job.department}` : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close interview details"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal body (scrolls) */}
        <div className="overflow-y-auto bg-slate-50/60 p-3 sm:p-4">
          <InterviewDetails
            interview={interview}
            job={job}
            applicationStatus={applicationStatus}
            className=""
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ */
/* Documents (resume + intro video) for this application               */
/* ------------------------------------------------------------------ */

function getFileNameFromResponse(
  response: { headers?: Record<string, unknown> } | undefined,
  fallback: string
) {
  const disposition = response?.headers?.["content-disposition"];

  if (typeof disposition === "string") {
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encoded?.[1]) {
      try {
        return decodeURIComponent(encoded[1]);
      } catch (error) {
      }
    }

    const plain = disposition.match(/filename="?([^";]+)"?/i);
    if (plain?.[1]) return plain[1];
  }

  const type = String(response?.headers?.["content-type"] || "").toLowerCase();
  if (type.includes("pdf")) return `${fallback}.pdf`;
  if (type.includes("wordprocessingml")) return `${fallback}.docx`;
  if (type.includes("msword")) return `${fallback}.doc`;
  return fallback;
}

function VideoModal({
  applicationId,
  title,
  onClose,
}: {
  applicationId: string;
  title: string;
  onClose: () => void;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    const loadVideo = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        const response = await downloadCandidateVideo(applicationId);
        const contentType = response.headers?.["content-type"];

        const blob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data], {
              type: typeof contentType === "string" ? contentType : "video/mp4",
            });

        if (blob.size === 0) {
          throw new Error("The returned video file is empty.");
        }

        createdUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(createdUrl);
          return;
        }

        setObjectUrl(createdUrl);
      } catch (error) {
        if (!cancelled) {
          console.error("Candidate video loading failed:", error);
          setHasError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadVideo();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [applicationId, attempt]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-3 backdrop-blur-sm sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Introduction video"
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              Introduction video
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-500">{title}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close video"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 sm:p-5">
          <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl bg-slate-900">
            {isLoading ? (
              <div className="flex flex-col items-center gap-3 text-white">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <p className="text-sm">Loading video...</p>
              </div>
            ) : hasError || !objectUrl ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 text-center text-white">
                <Video size={28} />
                <p className="text-sm font-semibold">
                  Video is not available for this job
                </p>
                <p className="text-xs text-slate-300">
                  It may not be uploaded yet, or it could not be loaded.
                </p>
                <button
                  type="button"
                  onClick={() => setAttempt((value) => value + 1)}
                  className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold hover:bg-blue-700"
                >
                  Try again
                </button>
              </div>
            ) : (
              <video
                key={objectUrl}
                className="h-full w-full object-contain"
                src={objectUrl}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onError={() => setHasError(true)}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ApplicationDocuments({
  application,
}: {
  application: CandidateApplication;
}) {
  const [busy, setBusy] = useState<"preview" | "download" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  const jobTitle = application.job?.title || "Application";

  const fetchResume = async () => {
    const response = await downloadCandidateResume(application.id);
    const blob = response.data;

    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error("Invalid resume response");
    }

    return { response, blob };
  };

  const handlePreview = async () => {
    if (busy) return;

    try {
      setMessage(null);
      setBusy("preview");
      const { blob } = await fetchResume();
      openBlobInNewTab(blob);
    } catch (error) {
      console.error("Resume preview failed:", error);
      setMessage("Resume is not available for this job.");
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    if (busy) return;

    try {
      setMessage(null);
      setBusy("download");
      const { response, blob } = await fetchResume();
      downloadBlob(
        blob,
        getFileNameFromResponse(response, `Resume - ${jobTitle}`)
      );
    } catch (error) {
      console.error("Resume download failed:", error);
      setMessage("Resume is not available for this job.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3.5">
      <div className="flex flex-col gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
            <FileText size={15} />
          </div>

          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900">
              Submitted documents
            </p>
            <p className="text-[11px] text-slate-500">
              Resume and intro video for this job
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-stretch overflow-hidden rounded-md border border-slate-200 bg-white">
            <button
              type="button"
              onClick={handlePreview}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Eye size={13} />
              {busy === "preview" ? "Opening..." : "View resume"}
            </button>

            <button
              type="button"
              onClick={handleDownload}
              disabled={busy !== null}
              aria-label="Download resume"
              title="Download resume"
              className="inline-flex items-center justify-center border-l border-slate-200 px-2.5 text-slate-600 transition hover:bg-slate-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download size={13} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowVideo(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
          >
            <Play size={12} fill="currentColor" strokeWidth={0} />
            Watch video
          </button>
        </div>
      </div>

      {message && <p className="mt-1.5 text-xs text-red-600">{message}</p>}

      {showVideo && (
        <VideoModal
          applicationId={application.id}
          title={jobTitle}
          onClose={() => setShowVideo(false)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Application card                                                    */
/* ------------------------------------------------------------------ */

function ApplicationCard({
  application,
}: {
  application: CandidateApplication;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);

  const job = application.job;
  const interview = application.latestInterview;

  const normalizedStatus = normalizeStatus(application.status);

  const displayStatus =
    application.currentStatus || getStatusDisplayValue(application.status);

  const statusKey = displayStatus.toUpperCase();

  const initial = (job?.title || "J").trim().charAt(0).toUpperCase();

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      {/* Application Header */}
      <div className="flex items-center gap-3.5 px-4 py-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-base font-bold text-blue-600">
          {initial}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3
              className="min-w-0 truncate text-[15px] font-semibold text-slate-900"
              title={job?.title || "-"}
            >
              {job?.title || "-"}
            </h3>

            <StatusPill
              small
              className={
                statusStyles[statusKey] ||
                "bg-slate-100 text-slate-600 ring-slate-200"
              }
            >
              {displayStatus}
            </StatusPill>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>{job?.department || "Department not specified"}</span>

            {job?.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} className="text-slate-400" />
                {job.location}
              </span>
            )}

            {job?.employmentType && (
              <span className="inline-flex items-center gap-1">
                <Briefcase size={12} className="text-slate-400" />
                {formatEmploymentType(job.employmentType)}
              </span>
            )}

            <span className="inline-flex items-center gap-1">
              <CalendarDays size={12} className="text-slate-400" />
              Applied on {formatDate(application.createdAt)}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-label={expanded ? "Collapse application" : "Expand application"}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
        >
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 pb-4">
          {/* Progress */}
          <ProgressTimeline application={application} />

          {/* Current Interview */}
          {interview && (
            <InterviewSummaryRow
              interview={interview}
              job={job}
              onViewDetails={() => setShowInterviewModal(true)}
            />
          )}

          {/* Resume + Intro video of this application */}
          <ApplicationDocuments application={application} />
        </div>
      )}

      {showInterviewModal && interview && (
        <InterviewModal
          interview={interview}
          job={job}
          applicationStatus={displayStatus}
          onClose={() => setShowInterviewModal(false)}
        />
      )}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Page section                                                        */
/* ------------------------------------------------------------------ */

export default function AppliedJobs({
  applications = [],
}: AppliedJobsProps) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Section Header */}
      <div className="flex flex-col gap-2 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 sm:text-base">
            My applications
          </h2>

          <p className="mt-0.5 text-xs text-slate-500">
            Track your applications, interviews and recruitment progress
          </p>
        </div>

        <span className="w-fit rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-600">
          {applications.length}{" "}
          {applications.length === 1 ? "Application" : "Applications"}
        </span>
      </div>

      {/* Applications */}
      <div className="space-y-2.5 bg-slate-50/60 p-3">
        {applications.length === 0 ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-5 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-500">
              <Briefcase size={22} />
            </div>

            <p className="text-sm font-semibold text-slate-800">
              You haven't applied to any jobs yet
            </p>

            <p className="mt-1 max-w-sm text-xs leading-5 text-slate-500">
              Jobs you apply to will show up here with their status and
              interview details.
            </p>
          </div>
        ) : (
          applications.map((application) => (
            <ApplicationCard key={application.id} application={application} />
          ))
        )}
      </div>
    </section>
  );
}